import React, { useContext } from 'react';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useHistory, useParams } from 'react-router-dom';
import { Color, Title } from '@bb/elements-web';
import {
  SubscriptionsPageProvider,
  useSubscriptionsPageContext,
} from './SubscriptionsPageContext';
import PaymentCard from './components/PaymentCard';
import ContactDetailsCard from './components/ContactDetailsCard';
import AllSubscriptionsCard from './components/AllSubscriptionsCard';
import DocumentsCard from './components/DocumentsCard';
import InvoicesCard from './components/InvoicesCard';
import UpdatePaymentCardModalContextProvider from '../../components/ManagePaymentCard/UpdatePaymentCardModalContext';

/** Whether subscriptions are being managed for an individual user or
 * for an entire brokerage. Brokerage admins can go to both types of pages
 * depending on what URL the access it from. */
export type ViewingLevel = 'user' | 'org';

const SubscriptionsPaymentsPageContent: React.FC = () => {
  const history = useHistory();
  const { viewingLevel, canManageBilling } = useSubscriptionsPageContext();
  const { auth } = useContext(AuthContext);

  return (
    <StyledPageWrapper>
      <StyledHeader>
        <Title
          level={1}
          style={{ fontSize: '30px' }}
          dataCy="subscriptions-payments-page-header"
        >
          {viewingLevel === 'user'
            ? 'Subscriptions and Payments'
            : 'Brokerage Subscriptions and Payments'}
        </Title>
      </StyledHeader>
      <StyledCardsContainer>
        <StyledCardRow>
          <PaymentCard viewingLevel={viewingLevel} />
        </StyledCardRow>
        <StyledCardRow>
          <AllSubscriptionsCard
            userId={auth.id}
            viewingLevel={viewingLevel}
            onOrgPlanClick={() =>
              console.log(
                'Placeholder function here. Going to remove this when removing the Org Plan Learn More button.',
              )
            }
            onAnsweringServiceClick={() =>
              history.push(`/answering_service/${viewingLevel}`)
            }
            onSupraOneClick={() =>
              history.push(
                `/subscriptions_payments/${viewingLevel}/supra_access`,
              )
            }
          />
        </StyledCardRow>
        <StyledCardRow>
          <ContactDetailsCard canManageBilling={canManageBilling} />
        </StyledCardRow>
        <StyledCardRow>
          <InvoicesCard />
        </StyledCardRow>
        <StyledCardRow>
          <DocumentsCard />
        </StyledCardRow>
      </StyledCardsContainer>
    </StyledPageWrapper>
  );
};

/**
 * The page in which a user or a brokerage can manage all of their subscriptions
 * and payment methods as well as view all billing documents and invoices.
 */
const SubscriptionsPaymentsPage: React.FC = () => {
  // The viewing level must be sent in via the URL params, otherwise default to user.
  const { viewingLevel = 'user' } = useParams<{
    viewingLevel: ViewingLevel;
  }>();
  const { auth } = useContext(AuthContext);

  const billingCustomerNumber =
    viewingLevel === 'user'
      ? auth.billingCustomerNumber
      : auth.org?.billingCustomerNumber;

  // If we can't get their brokerage details, render nothing.
  if (!auth?.org?.id) {
    return null;
  }

  return (
    <SubscriptionsPageProvider
      billingCustomerNumber={billingCustomerNumber}
      orgId={auth.org.id}
      viewingLevel={viewingLevel}
    >
      <UpdatePaymentCardModalContextProvider>
        <SubscriptionsPaymentsPageContent />
      </UpdatePaymentCardModalContextProvider>
    </SubscriptionsPageProvider>
  );
};

export default SubscriptionsPaymentsPage;

const StyledPageWrapper = styled.div`
  padding: 24px;
  max-width: 1050px;
  margin: 0 auto;
`;

const StyledHeader = styled.div`
  margin-bottom: 24px;
  h1 {
    margin: 0;
    font-size: 24px;
    font-weight: 500;
    color: ${Color.BLACK};
  }
`;

const StyledCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  &:has(> :only-child) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
